// shared/common/api.ts
import { API_BASE_URL, STORAGE_PREFIX } from "@config";
import { emitRateLimit } from "./rateLimitEmitter";

const k = (name) => `${STORAGE_PREFIX}${name}`;
const envTimeout = Number.parseInt(import.meta.env.VITE_FETCH_TIMEOUT_MS ?? "", 10);
const DEFAULT_FETCH_TIMEOUT_MS = Number.isFinite(envTimeout) && envTimeout > 0 ? envTimeout : 30000;
const TIMEOUT_RETRY_DELAY_MS = 250;

function makeAbortReason(message, name = "AbortError") {
  if (typeof DOMException === "function") {
    return new DOMException(message, name);
  }
  const error = new Error(message);
  error.name = name;
  return error;
}

function createRequestSignal(externalSignal, timeoutMs = DEFAULT_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  let timeoutId = null;

  const abort = (reason) => {
    if (!controller.signal.aborted) {
      controller.abort(reason);
    }
  };

  const onExternalAbort = () => {
    abort(externalSignal?.reason ?? makeAbortReason("Request aborted"));
  };

  if (externalSignal?.aborted) {
    onExternalAbort();
  } else if (externalSignal) {
    externalSignal.addEventListener("abort", onExternalAbort, { once: true });
  }

  if (timeoutMs > 0) {
    timeoutId = window.setTimeout(() => {
      abort(makeAbortReason(`Request timed out after ${timeoutMs}ms`, "TimeoutError"));
    }, timeoutMs);
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      if (externalSignal) externalSignal.removeEventListener("abort", onExternalAbort);
    },
  };
}

function isTimeoutError(error) {
  return (
    error?.name === "TimeoutError" ||
    (error?.name === "AbortError" && String(error?.message || "").toLowerCase().includes("timed out"))
  );
}

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? makeAbortReason("Request aborted"));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    const onAbort = () => {
      cleanup();
      reject(signal?.reason ?? makeAbortReason("Request aborted"));
    };

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      if (signal) signal.removeEventListener("abort", onAbort);
    };

    if (signal) {
      signal.addEventListener("abort", onAbort, { once: true });
    }
  });
}

// ⚠️ logout forcé ici, utilisé quand le refresh échoue
function hardLogout() {
  ["token", "refresh_token", "user_id", "club_id", "app"].forEach((key) =>
    localStorage.removeItem(k(key))
  );
  // route /login existe dans trainer, et dans monitoring ça tombera
  // sur la route catch-all qui affiche aussi la page de login
  window.location.href = "/login";
}

const refreshToken = async (signal) => {
  const refresh_token = localStorage.getItem(k("refresh_token"));
  if (!refresh_token) {
   
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/refresh`, {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${refresh_token}`,
      },
    });

    const data = await response.json();
    if (response.ok && data.access_token) {
      localStorage.setItem(k("token"), data.access_token);
      return data.access_token;
    } else {
     
      return null;
    }
  } catch (error) {
    if (signal?.aborted || error?.name === "AbortError" || error?.name === "TimeoutError") {
      throw error;
    }
    
    return null;
  }
};

// 👉 Fonction à utiliser partout dans les composants (shared ou non)
export const fetchAuth = async (url, options = {}) => {
  const {
    timeoutMs = DEFAULT_FETCH_TIMEOUT_MS,
    signal: externalSignal,
    ...fetchOptions
  } = options;
  const token = localStorage.getItem(k("token"));
  if (!token) throw new Error("Not authenticated");

  const finalUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;

  const headers = {
    ...(fetchOptions.headers || {}),
    Authorization: `Bearer ${token}`,
  };

  const hasBody = fetchOptions.body !== undefined && fetchOptions.body !== null;
  const isFormData = fetchOptions.body instanceof FormData;

  if (hasBody && !isFormData && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const request = createRequestSignal(externalSignal, timeoutMs);
  const method = String(fetchOptions.method || "GET").toUpperCase();
  const canRetryOnTimeout = method === "GET" || method === "HEAD";

  const runRequest = async (authToken) => {
    return fetch(finalUrl, {
      ...fetchOptions,
      headers: {
        ...headers,
        Authorization: `Bearer ${authToken}`,
      },
      signal: request.signal,
    });
  };

  try {
    let response;

    try {
      response = await runRequest(token);
    } catch (error) {
      if (!canRetryOnTimeout || !isTimeoutError(error) || request.signal.aborted) {
        throw error;
      }

      await delay(TIMEOUT_RETRY_DELAY_MS, request.signal);
      response = await runRequest(token);
    }

    // ── Rate-limit detection (429) ──
    if (response.status === 429) {
      try {
        const body = await response.clone().json();
        const retryAfter = body.retry_after ?? 60;
        const reason = (body.reason ?? "").toLowerCase();

        if (reason.includes("global")) {
          emitRateLimit("global_block", retryAfter);
        } else {
          emitRateLimit("route_block", retryAfter);
        }
      } catch {
        emitRateLimit("route_block", 120);
      }
      return response;
    }

    if (response.status === 401 || response.status === 422) {
      const newToken = await refreshToken(request.signal);
      if (newToken) {
        response = await runRequest(newToken);
      } else {
        hardLogout();
        throw new Error("Token not refreshable");
      }
    }

    return response;
  } catch (error) {
    if (request.signal.aborted) {
      throw request.signal.reason ?? error;
    }
    throw error;
  } finally {
    request.cleanup();
  }
};
